import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocumentBrowserStore } from './useDocumentBrowserStore';
import { DocumentCardGrid } from './DocumentCardGrid';
import { DocumentFilterSidebar } from './DocumentFilterSidebar';
import { DocumentTable } from './DocumentTable';
import { DocumentTimeline } from './DocumentTimeline';
import { DocumentDetailsDialog } from './DocumentDetailsDialog';
import {
  isActiveDocumentStatus,
  isConnectorSource,
  isProblemDocumentStatus,
  isProcessingDocumentStatus,
  isReviewDocumentStatus,
} from './documentBrowserUtils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useApiToken } from '@/auth/hooks';
import { PluginPageHeader } from '@/app/components/PluginPageHeader';
import { PluginMetricCards } from '@/app/components/PluginMetricCards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { DocumentDetailDto } from '@/types/document';
import { DocumentViewerDialog } from '@/app/features/talk-to-data/components/DocumentViewerDialog';
import type { DocumentGroupingMode, DocumentSortMode } from './useDocumentBrowserStore';
import { AlertTriangle, ArrowUpDown, CheckCircle2, ChevronLeft, ChevronRight, Clock, FileText, Filter, Layers3, LayoutGrid, LayoutList, Search } from 'lucide-react';

const GROUPING_OPTIONS: Array<{ value: DocumentGroupingMode; label: string }> = [
  { value: 'none', label: 'Keine Gruppierung' },
  { value: 'machine', label: 'Nach Maschine' },
  { value: 'documentType', label: 'Nach Typ' },
  { value: 'status', label: 'Nach Status' },
];

const SORT_OPTIONS: Array<{ value: DocumentSortMode; label: string }> = [
  { value: 'createdAtDesc', label: 'Neueste zuerst' },
  { value: 'createdAtAsc', label: 'Aelteste zuerst' },
  { value: 'fileNameAsc', label: 'Dateiname A-Z' },
  { value: 'fileNameDesc', label: 'Dateiname Z-A' },
];

export function DocumentBrowserPage() {
  const [searchParams] = useSearchParams();
  const activeWorkspace = useWorkspaceStore((s) => s.activeWorkspace);
  const { getAccessToken } = useApiToken();

  const documents = useDocumentBrowserStore((s) => s.documents);
  const totalCount = useDocumentBrowserStore((s) => s.totalCount);
  const availableDocumentTypes = useDocumentBrowserStore((s) => s.availableDocumentTypes);
  const availableLanguages = useDocumentBrowserStore((s) => s.availableLanguages);
  const availableMachines = useDocumentBrowserStore((s) => s.availableMachines);
  const facetCounts = useDocumentBrowserStore((s) => s.facetCounts);
  const page = useDocumentBrowserStore((s) => s.page);
  const pageSize = useDocumentBrowserStore((s) => s.pageSize);
  const totalPages = useDocumentBrowserStore((s) => s.totalPages);
  const loading = useDocumentBrowserStore((s) => s.loading);
  const error = useDocumentBrowserStore((s) => s.error);
  const filters = useDocumentBrowserStore((s) => s.filters);
  const viewMode = useDocumentBrowserStore((s) => s.viewMode);
  const groupBy = useDocumentBrowserStore((s) => s.groupBy);
  const sortBy = useDocumentBrowserStore((s) => s.sortBy);
  const loadDocuments = useDocumentBrowserStore((s) => s.loadDocuments);
  const setPage = useDocumentBrowserStore((s) => s.setPage);
  const setFilters = useDocumentBrowserStore((s) => s.setFilters);
  const setViewMode = useDocumentBrowserStore((s) => s.setViewMode);
  const setGroupBy = useDocumentBrowserStore((s) => s.setGroupBy);
  const setSortBy = useDocumentBrowserStore((s) => s.setSortBy);
  const resetFilters = useDocumentBrowserStore((s) => s.resetFilters);

  const initialRef = useRef(false);
  const [selectedDocument, setSelectedDocument] = useState<(typeof documents)[number] | null>(null);
  const [selectedDocumentDetail, setSelectedDocumentDetail] = useState<DocumentDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<(typeof documents)[number] | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const requestedDocumentId = searchParams.get('documentId');

  const metrics = useMemo(() => {
    return {
      visible: documents.length,
      active: documents.filter((doc) => isActiveDocumentStatus(doc.status)).length,
      processing: documents.filter((doc) => isProcessingDocumentStatus(doc.status)).length,
      review: documents.filter((doc) => isReviewDocumentStatus(doc.status)).length,
      problems: documents.filter((doc) => isProblemDocumentStatus(doc.status)).length,
      connector: documents.filter((doc) => isConnectorSource(doc.sourceKind)).length,
    };
  }, [documents]);

  // Load on mount and when page/filters change
  useEffect(() => {
    if (!activeWorkspace) return;
    const load = async () => {
      const token = await getAccessToken();
      await loadDocuments(activeWorkspace.id, token);
    };
    if (!initialRef.current) {
      initialRef.current = true;
      load();
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace?.id, page, filters]);

  useEffect(() => {
    if (!selectedDocument) {
      setSelectedDocumentDetail(null);
      setDetailLoading(false);
      return;
    }

    if (selectedDocumentDetail?.id === selectedDocument.id) {
      setDetailLoading(false);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setSelectedDocumentDetail(null);

    const loadDetail = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(`/api/dotnet/documents/${selectedDocument.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Fehler beim Laden der Dokumentdetails (${response.status})`);
        }

        const data = (await response.json()) as DocumentDetailDto;
        if (!cancelled) {
          setSelectedDocumentDetail(data);
        }
      } catch {
        if (!cancelled) {
          setSelectedDocumentDetail(null);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    void loadDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedDocument, getAccessToken]);

  useEffect(() => {
    if (!requestedDocumentId || !activeWorkspace) {
      return;
    }

    if (selectedDocument?.id === requestedDocumentId) {
      return;
    }

    let cancelled = false;
    const loadRequestedDocument = async () => {
      setDetailLoading(true);
      try {
        const token = await getAccessToken();
        const response = await fetch(`/api/dotnet/documents/${requestedDocumentId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Fehler beim Laden der Dokumentdetails (${response.status})`);
        }

        const data = (await response.json()) as DocumentDetailDto;
        if (!cancelled) {
          setSelectedDocument(data);
          setSelectedDocumentDetail(data);
        }
      } catch {
        if (!cancelled) {
          setSelectedDocument(null);
          setSelectedDocumentDetail(null);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    void loadRequestedDocument();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspace, getAccessToken, requestedDocumentId, selectedDocument?.id]);

  const handleSearch = (value: string) => {
    setFilters({ search: value });
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleDownloadDocument = async (selectedDoc: (typeof documents)[number]) => {
    const token = await getAccessToken();
    const response = await fetch(`/api/dotnet/documents/${selectedDoc.id}/view`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = blobUrl;
    link.download = selectedDoc.fileName;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const handleOpenPreview = (document: (typeof documents)[number]) => {
    setSelectedDocument(null);
    setPreviewDocument(document);
  };

  const hasActiveFilters = !!(filters.search || filters.status || filters.sourceKind || filters.documentType || filters.language || filters.machineModelId);
  const metricItems = [
    { id: 'total', label: 'Gesamt', value: totalCount, detail: 'Dokumente im Workspace', icon: FileText, tone: 'neutral' as const },
    { id: 'active', label: 'Aktiv', value: metrics.active, detail: 'Abgeschlossene Dokumente auf dieser Seite', icon: CheckCircle2, tone: 'success' as const },
    { id: 'processing', label: 'In Verarbeitung', value: metrics.processing, detail: 'Uploads, Parsing und Extraktion', icon: Clock, tone: 'warning' as const },
    { id: 'review', label: 'Review', value: metrics.review, detail: 'Warten noch auf Bestaetigung', icon: Layers3, tone: 'info' as const },
    { id: 'problems', label: 'Probleme', value: metrics.problems, detail: 'Fehler, Teilfehler oder entfernte Dokumente', icon: AlertTriangle, tone: 'danger' as const },
  ];
  const primaryMetric = metricItems[0];
  const secondaryMetrics = metricItems.slice(1);

  return (
    <div className="flex w-full min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
      <PluginPageHeader
        group="admin"
        title="Sopho Data Viewer"
        icon={FileText}
      />

      {showFilters ? (
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(260px,1fr)_minmax(0,4fr)]">
          <div className="space-y-6 xl:col-start-1 xl:row-start-1">
            <PluginMetricCards
              className="grid-cols-1 lg:grid-cols-1"
              items={[primaryMetric]}
            />

            <aside className="xl:sticky xl:top-6">
              <DocumentFilterSidebar
                availableDocumentTypes={availableDocumentTypes}
                availableLanguages={availableLanguages}
                availableMachines={availableMachines}
                facetCounts={facetCounts}
                filters={filters}
                onStatusChange={(value) => setFilters({ status: value })}
                onSourceChange={(value) => setFilters({ sourceKind: value })}
                onDocumentTypeChange={(value) => setFilters({ documentType: value })}
                onLanguageChange={(value) => setFilters({ language: value })}
                onMachineChange={(value) => setFilters({ machineModelId: value })}
                onReset={resetFilters}
              />
            </aside>
          </div>

          <div className="min-w-0 space-y-6 xl:col-start-2 xl:row-start-1">
            <PluginMetricCards
              className="gap-3 md:grid-cols-2 2xl:grid-cols-4"
              items={secondaryMetrics}
            />

            <div className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm 2xl:flex-row 2xl:items-center 2xl:justify-between">
              <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                <Button variant="outline" size="sm" onClick={() => setShowFilters((value) => !value)} className="gap-2 lg:self-start">
                  <Filter className="h-4 w-4" />
                  {showFilters ? 'Filter ausblenden' : 'Filter einblenden'}
                </Button>
                <div className="relative min-w-0 flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Dokumente suchen…"
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-end">
                {hasActiveFilters && <span className="text-sm text-muted-foreground">Facetten aktiv</span>}
                <Select value={groupBy} onValueChange={(value) => setGroupBy(value as DocumentGroupingMode)} disabled={viewMode === 'timeline'}>
                  <SelectTrigger aria-label="Gruppierung" className="w-full min-w-[190px] lg:w-[210px]">
                    <div className="flex items-center gap-2">
                      <Layers3 className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="Gruppierung" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {GROUPING_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value as DocumentSortMode)} disabled={viewMode === 'timeline'}>
                  <SelectTrigger aria-label="Sortierung" className="w-full min-w-[190px] lg:w-[210px]">
                    <div className="flex items-center gap-2">
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="Sortierung" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap items-center gap-1 rounded-md bg-muted/40 p-1">
                  <Button
                    size="sm"
                    variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('table')}
                    className="gap-1"
                  >
                    <LayoutList className="h-4 w-4" />
                    Tabelle
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('cards')}
                    className="gap-1"
                  >
                    <LayoutGrid className="h-4 w-4" />
                    Karten
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === 'timeline' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('timeline')}
                    className="gap-1"
                  >
                    <Clock className="h-4 w-4" />
                    Timeline
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>
                  <p className="font-medium">Dokumente konnten nicht vollstaendig geladen werden.</p>
                  <p className="text-muted-foreground">{error}</p>
                </div>
              </div>
            )}

            {viewMode === 'table' ? (
              <DocumentTable documents={documents} loading={loading} groupBy={groupBy} sortBy={sortBy} onSelectDocument={setSelectedDocument} />
            ) : viewMode === 'cards' ? (
              <DocumentCardGrid documents={documents} loading={loading} groupBy={groupBy} sortBy={sortBy} onSelectDocument={setSelectedDocument} />
            ) : (
              <DocumentTimeline documents={documents} onSelectDocument={setSelectedDocument} />
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Seite {page} von {totalPages} · {pageSize} pro Seite
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={page <= 1}>
                    <ChevronLeft className="h-4 w-4" />
                    Zurück
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleNextPage} disabled={page >= totalPages}>
                    Weiter
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <PluginMetricCards
            className="gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5"
            items={metricItems}
          />

          <div className="min-w-0 space-y-6">
            <div className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm 2xl:flex-row 2xl:items-center 2xl:justify-between">
              <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                <Button variant="outline" size="sm" onClick={() => setShowFilters((value) => !value)} className="gap-2 lg:self-start">
                  <Filter className="h-4 w-4" />
                  {showFilters ? 'Filter ausblenden' : 'Filter einblenden'}
                </Button>
                <div className="relative min-w-0 flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Dokumente suchen…"
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-end">
                {hasActiveFilters && <span className="text-sm text-muted-foreground">Facetten aktiv</span>}
                <Select value={groupBy} onValueChange={(value) => setGroupBy(value as DocumentGroupingMode)} disabled={viewMode === 'timeline'}>
                  <SelectTrigger aria-label="Gruppierung" className="w-full min-w-[190px] lg:w-[210px]">
                    <div className="flex items-center gap-2">
                      <Layers3 className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="Gruppierung" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {GROUPING_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value as DocumentSortMode)} disabled={viewMode === 'timeline'}>
                  <SelectTrigger aria-label="Sortierung" className="w-full min-w-[190px] lg:w-[210px]">
                    <div className="flex items-center gap-2">
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="Sortierung" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap items-center gap-1 rounded-md bg-muted/40 p-1">
                  <Button
                    size="sm"
                    variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('table')}
                    className="gap-1"
                  >
                    <LayoutList className="h-4 w-4" />
                    Tabelle
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('cards')}
                    className="gap-1"
                  >
                    <LayoutGrid className="h-4 w-4" />
                    Karten
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === 'timeline' ? 'secondary' : 'ghost'}
                    onClick={() => setViewMode('timeline')}
                    className="gap-1"
                  >
                    <Clock className="h-4 w-4" />
                    Timeline
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>
                  <p className="font-medium">Dokumente konnten nicht vollstaendig geladen werden.</p>
                  <p className="text-muted-foreground">{error}</p>
                </div>
              </div>
            )}

            {viewMode === 'table' ? (
              <DocumentTable documents={documents} loading={loading} groupBy={groupBy} sortBy={sortBy} onSelectDocument={setSelectedDocument} />
            ) : viewMode === 'cards' ? (
              <DocumentCardGrid documents={documents} loading={loading} groupBy={groupBy} sortBy={sortBy} onSelectDocument={setSelectedDocument} />
            ) : (
              <DocumentTimeline documents={documents} onSelectDocument={setSelectedDocument} />
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Seite {page} von {totalPages} · {pageSize} pro Seite
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={page <= 1}>
                    <ChevronLeft className="h-4 w-4" />
                    Zurück
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleNextPage} disabled={page >= totalPages}>
                    Weiter
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <DocumentDetailsDialog
        document={selectedDocument}
        detail={selectedDocumentDetail}
        loading={detailLoading}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedDocument(null);
          }
        }}
        onOpenPreview={handleOpenPreview}
        onDownload={(document) => void handleDownloadDocument(document)}
      />

      <DocumentViewerDialog
        documentId={previewDocument?.id ?? null}
        fileName={previewDocument?.fileName}
        onClose={() => setPreviewDocument(null)}
      />
    </div>
  );
}
